import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Shirt, Package, Wrench, ExternalLink, MessageCircle, Phone, Mail } from "lucide-react";

const ACC_IMG = "https://ls39motos.com/wp-content/uploads/2025/04/2024-Yamaha-MT07TR-EU-Midnight_Black-Static-001-03.jpg";
const ROPA_IMG = "https://ls39motos.com/wp-content/uploads/2025/04/ls-39-motos-yamaha-ropa-02.jpeg";

export default function Products() {
    const { lang } = useLang();

    const t = lang === "es" ? {
        overline: "Productos Oficiales Yamaha · Mallorca",
        title: "Accesorios, ropa y recambios",
        intro: "En LS39 Motos te ofrecemos toda la gama de productos oficiales Yamaha en Mallorca: accesorios para personalizar y mejorar tu moto, ropa diseñada para protegerte con estilo, y recambios originales con la garantía del concesionario oficial.",
        sub1: "En cada kilómetro vive la aventura.",
        // Accesorios
        acc_title: "Accesorios Yamaha",
        acc_p1: "Yamaha ofrece una amplia gama de accesorios originales que mejoran el rendimiento y personalizan tu moto.",
        acc_p2: "Desde sistemas de escape hasta equipamiento de seguridad, cada accesorio está diseñado para optimizar tu experiencia de conducción.",
        acc_cta: "Explorar accesorios Yamaha",
        // Ropa
        ropa_title: "Ropa y equipamiento",
        ropa_p1: "Yamaha ofrece una exclusiva colección de ropa y equipamiento diseñados para brindarte comodidad y protección en cada viaje.",
        ropa_p2: "Desde chaquetas y guantes hasta cascos y botas, cada prenda combina innovación y calidad para mejorar tu experiencia de conducción.",
        ropa_cta: "Explorar ropa Yamaha",
        // Recambios
        rec_title: "Recambios originales",
        rec_p1: "Contamos con un taller oficial Yamaha, donde seguimos rigurosamente los estándares de calidad y atención exigidos por la marca.",
        rec_p2: "Recambios originales con garantía oficial — instalados por nuestros mecánicos certificados Yamaha en Palma de Mallorca.",
        rec_cta_wa: "WhatsApp recambios",
        rec_cta_mail: "Email",
        rec_cta_tel: "Llamar",
        rec_hours_title: "Horario Recambios",
        rec_hours: "Lunes – Viernes: 8:00 – 16:00",
        rec_address: "Gran Via Asima, 35 · Palma de Mallorca",
        contact_block: "¿Necesitas un recambio o un accesorio específico? Escríbenos por WhatsApp y te lo conseguimos.",
        overline_acc: "Accesorios",
        overline_apparel: "Ropa",
        overline_parts: "Recambios · Servicio Autorizado Yamaha",
    } : {
        overline: "Official Yamaha Products · Mallorca",
        title: "Accessories, apparel and spare parts",
        intro: "At LS39 Motos we offer you the full range of official Yamaha products in Mallorca: accessories to personalise and upgrade your bike, apparel designed to protect you in style, and original spare parts backed by the official dealership warranty.",
        sub1: "Adventure lives in every kilometre.",
        // Accesorios
        acc_title: "Yamaha accessories",
        acc_p1: "Yamaha offers a wide range of original accessories that enhance performance and personalise your bike.",
        acc_p2: "From exhaust systems to safety gear, every accessory is designed to optimise your riding experience.",
        acc_cta: "Explore Yamaha accessories",
        // Apparel
        ropa_title: "Apparel & gear",
        ropa_p1: "Yamaha offers an exclusive collection of apparel and gear designed to deliver comfort and protection on every ride.",
        ropa_p2: "From jackets and gloves to helmets and boots, every item combines innovation and quality to elevate your ride.",
        ropa_cta: "Explore Yamaha apparel",
        // Recambios
        rec_title: "Original spare parts",
        rec_p1: "We operate an official Yamaha workshop, strictly following the quality and service standards required by the brand.",
        rec_p2: "Original spare parts with official warranty — installed by our Yamaha-certified mechanics in Palma de Mallorca.",
        rec_cta_wa: "Parts WhatsApp",
        rec_cta_mail: "Email",
        rec_cta_tel: "Call",
        rec_hours_title: "Parts Counter Hours",
        rec_hours: "Monday – Friday: 8:00 – 16:00",
        rec_address: "Gran Via Asima, 35 · Palma de Mallorca",
        contact_block: "Need a specific spare or accessory? WhatsApp us and we'll source it for you.",
        overline_acc: "Accessories",
        overline_apparel: "Apparel",
        overline_parts: "Parts · Authorised Yamaha Service",
    };

    return (
        <div data-testid="products-page">
            {/* HERO */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
                <p className="text-overline">{t.overline}</p>
                <h1 className="mt-6 font-display text-5xl font-bold tracking-tighter sm:text-6xl lg:text-7xl">
                    {t.title}
                </h1>
                <p className="mt-6 max-w-2xl text-base text-neutral-600 leading-relaxed">{t.intro}</p>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em]" style={{ color: "var(--ls-terracotta)" }}>
                    {t.sub1}
                </p>
            </section>

            {/* Accesorios */}
            <section id="accesorios" className="border-t border-black/10" data-testid="products-accessories">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-6">
                            <div className="relative aspect-[4/3] overflow-hidden border border-black/10">
                                <img src={ACC_IMG} alt="Yamaha accessories" className="h-full w-full object-cover" />
                            </div>
                        </div>
                        <div className="lg:col-span-6 flex flex-col justify-center">
                            <p className="text-overline flex items-center gap-2">
                                <Package size={14} /> {t.overline_acc}
                            </p>
                            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight">{t.acc_title}</h2>
                            <p className="mt-6 text-base text-neutral-600">{t.acc_p1}</p>
                            <p className="mt-3 text-base text-neutral-600">{t.acc_p2}</p>
                            <a
                                href="https://www.yamaha-motor.eu/es/es/accessories/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary mt-8 self-start"
                                data-testid="products-acc-cta"
                            >
                                {t.acc_cta} <ExternalLink size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ropa */}
            <section id="ropa" className="border-t border-black/10 bg-[--ls-paper]" data-testid="products-apparel">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-6 order-2 lg:order-1 flex flex-col justify-center">
                            <p className="text-overline flex items-center gap-2">
                                <Shirt size={14} /> {t.overline_apparel}
                            </p>
                            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight">{t.ropa_title}</h2>
                            <p className="mt-6 text-base text-neutral-600">{t.ropa_p1}</p>
                            <p className="mt-3 text-base text-neutral-600">{t.ropa_p2}</p>
                            <a
                                href="https://www.yamaha-motor.eu/es/es/clothing-merchandise/clothing/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-accent mt-8 self-start"
                                data-testid="products-ropa-cta"
                            >
                                {t.ropa_cta} <ExternalLink size={14} />
                            </a>
                        </div>
                        <div className="lg:col-span-6 order-1 lg:order-2">
                            <div className="relative aspect-[4/3] overflow-hidden border border-black/10">
                                <img src={ROPA_IMG} alt="Yamaha apparel" className="h-full w-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Recambios */}
            <section id="recambios" className="border-t border-black/10" data-testid="products-parts">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                    <p className="text-overline flex items-center gap-2">
                        <Wrench size={14} /> {t.overline_parts}
                    </p>
                    <h2 className="mt-4 font-display text-4xl font-bold tracking-tight">{t.rec_title}</h2>
                    <p className="mt-6 max-w-3xl text-base text-neutral-600">{t.rec_p1}</p>
                    <p className="mt-3 max-w-3xl text-base text-neutral-600">{t.rec_p2}</p>

                    <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <a
                            href="https://wa.me/34669559924"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="glass p-6 flex items-start gap-4 hover:border-black/30 transition-colors"
                            data-testid="parts-whatsapp"
                        >
                            <MessageCircle size={20} style={{ color: "#25D366" }} className="mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                    {t.rec_cta_wa}
                                </p>
                                <p className="mt-1 font-display text-lg">+34 669 559 924</p>
                            </div>
                        </a>
                        <a
                            href="mailto:recambios@ls39motos.com"
                            className="glass p-6 flex items-start gap-4 hover:border-black/30 transition-colors"
                            data-testid="parts-email"
                        >
                            <Mail size={20} style={{ color: "var(--ls-terracotta)" }} className="mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                    {t.rec_cta_mail}
                                </p>
                                <p className="mt-1 font-display text-lg break-all">recambios@ls39motos.com</p>
                            </div>
                        </a>
                        <a
                            href="tel:+34871005111"
                            className="glass p-6 flex items-start gap-4 hover:border-black/30 transition-colors"
                            data-testid="parts-phone"
                        >
                            <Phone size={20} style={{ color: "var(--ls-blue)" }} className="mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                    {t.rec_cta_tel}
                                </p>
                                <p className="mt-1 font-display text-lg">871 005 111</p>
                            </div>
                        </a>
                    </div>

                    <div className="mt-10 border border-black/10 p-6">
                        <p className="text-overline">{t.rec_hours_title}</p>
                        <p className="mt-3 font-mono text-base text-neutral-700">{t.rec_hours}</p>
                        <p className="mt-2 font-mono text-sm text-neutral-500">{t.rec_address}</p>
                    </div>

                    <p className="mt-8 text-sm text-neutral-500 italic max-w-2xl">{t.contact_block}</p>
                </div>
            </section>
        </div>
    );
}
